import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "./Navbar";
import SliderContactUs from "../pages/SliderContactUs";

const slides = [
    {
        id: 1,
        title: "Transform Your Online Presence",
        highlight: "Digital Marketing",
        description:
            "We are a passionate team of digital marketers dedicated to elevating your brand's visibility and engagement across all digital channels.",
        image:
            "/assets/img/DigitalMarketing.jpg",
        buttonText: "Explore Digital Services",
    },
    {
        id: 2,
        title: "Web & Mobile App",
        highlight: "Development",
        description:
            "Building modern, responsive web and mobile applications tailored to your business needs, ensuring seamless user experience and scalability.",
        image: "/assets/img/WebMobileApp.jpg",
        buttonText: "See Our Tech Solutions",
    },
    {
        id: 3,
        title: "Innovative Tech Solutions",
        highlight: "AI & Cloud",
        description:
            "Delivering cutting-edge AI and cloud-based solutions that drive efficiency, foster innovation, and unlock new possibilities for your enterprise.",
        image: "/assets/img/AICloud.jpg",
        buttonText: "See Our AI & Cloud Solutions",
    },
    {
        id: 4,
        title: "SEO",
        highlight: "Optimization",
        description:
            "Delivering cutting-edge SEO solutions that drive efficiency, foster innovation, and unlock new possibilities for your enterprise.",
        image: "/assets/img/SEO.jpg",
        buttonText: "See Our SEO Solutions",
    },
    {
        id: 5,
        title: "Outcome-Oriented Consulting",
        highlight: "Business Strategy",
        description:
            "We help companies navigate digital disruption, optimize operations, and formulate winning strategies for sustainable growth and competitive advantage.",
        image: "/assets/img/BusinessStrategy.jpg",
        buttonText: "Discover Our Approach",
    },
];

const HeroSection = () => {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [isContactOpen, setIsContactOpen] = useState(false);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slides.length);
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    const slideContentVariants = {
        initial: { opacity: 0, y: 50 },
        animate: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" },
        },
        exit: { opacity: 0, y: -50, transition: { duration: 0.5, ease: "easeIn" } },
    };

    const imageVariants = {
        initial: { opacity: 0, scale: 0.9, x: 50 },
        animate: {
            opacity: 1,
            scale: 1,
            x: 0,
            transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 },
        },
        exit: {
            opacity: 0,
            scale: 0.9,
            x: 50,
            transition: { duration: 0.5, ease: "easeIn" },
        },
    };

    const contactSliderVariants = {
        hidden: { x: "100%" },
        visible: { x: 0, transition: { type: "spring", stiffness: 70, damping: 20 } },
        exit: { x: "100%", transition: { type: "spring", stiffness: 70, damping: 20 } },
    };

    const currentSlide = slides[currentSlideIndex];

    return (
        <div className="relative font-inter min-h-screen flex flex-col">
            <Navbar />

            {/* Hero Slider with Enhanced Design */}
            <div className="flex-1 relative w-full h-full min-h-[calc(100vh-64px)] md:min-h-screen flex items-center justify-center py-16 px-6 md:px-12 overflow-hidden">
                {/* Enhanced Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50 opacity-95 z-0"></div>

                {/* Decorative Wave Patterns - Background */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                    {/* Top Wave */}
                    <svg className="absolute top-0 left-0 w-full h-64 opacity-5" viewBox="0 0 1440 320" preserveAspectRatio="none">
                        <path fill="currentColor" className="text-purple-500" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,138.7C960,139,1056,117,1152,96C1248,75,1344,53,1392,42.7L1440,32L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
                    </svg>
                    
                    {/* Bottom Wave */}
                    <svg className="absolute bottom-0 left-0 w-full h-64 opacity-5" viewBox="0 0 1440 320" preserveAspectRatio="none">
                        <path fill="currentColor" className="text-blue-500" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                    </svg>

                    {/* Floating Gradient Orbs */}
                    <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl animate-blob"></div>
                    <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
                    <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-br from-indigo-400/20 to-purple-400/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
                </div>

                {/* Enhanced Dot Grid Pattern */}
                <div className="absolute inset-0 z-0 opacity-10">
                    <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        <pattern
                            id="dot-grid"
                            width="30"
                            height="30"
                            patternUnits="userSpaceOnUse"
                        >
                            <circle cx="2" cy="2" r="1" fill="#6366f1" />
                        </pattern>
                        <rect width="100%" height="100%" fill="url(#dot-grid)" />
                    </svg>
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentSlide.id}
                        className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12 relative z-10 w-full"
                        initial="initial"
                        animate="animate"
                        exit="exit"
                    >
                        <motion.div
                            className="w-full md:w-1/2 text-center md:text-left space-y-6"
                            variants={slideContentVariants}
                        >
                            {/* Decorative Badge */}
                            <motion.div 
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full border border-purple-200/50 shadow-sm"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <span className="font-heading text-xs text-purple-900 uppercase tracking-wider">
                                    Premium Solutions
                                </span>
                            </motion.div>

                            {/* Enhanced Main Heading with font-display */}
                            <motion.h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-tight text-gray-900">
                                {currentSlide.title}{" "}
                                <span className="block mt-2">with{" "}
                                    <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent animate-gradient">
                                        {currentSlide.highlight}
                                    </span>
                                </span>
                            </motion.h1>

                            {/* Description with font-mono-body */}
                            <motion.p className="font-mono-body text-gray-700 text-base md:text-lg leading-relaxed max-w-xl mx-auto md:mx-0">
                                {currentSlide.description}
                            </motion.p>

                            {/* Enhanced CTA Button */}
                            <motion.button
                                className="group inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-transparent text-base md:text-lg font-heading font-bold rounded-full shadow-xl
                                text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-size-200 bg-pos-0 hover:bg-pos-100
                                focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-purple-500
                                transition-all duration-500 ease-in-out transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setIsContactOpen(true)}
                            >
                                <span>Contact Us</span>
                                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </motion.button>
                        </motion.div>

                        <motion.div
                            className="w-full md:w-1/2 mt-12 md:mt-0 flex justify-center p-4 relative"
                            variants={imageVariants}
                        >
                            {/* Enhanced Image Container with Multiple Decorative Layers */}
                            <div className="relative group">
                                {/* Main Image */}
                                <img
                                    src={currentSlide.image}
                                    alt={currentSlide.title}
                                    className="relative w-full max-w-lg object-contain rounded-3xl shadow-2xl border border-gray-100 transform rotate-3 group-hover:rotate-0 transition-all duration-500 z-10"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src =
                                            "https://placehold.co/800x600/cccccc/333333?text=Image+Error";
                                    }}
                                />
                                
                                {/* Decorative Background Layers */}
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/30 to-purple-400/30 rounded-3xl transform translate-x-4 translate-y-4 rotate-6 group-hover:rotate-3 transition-transform duration-500 hidden md:block"></div>
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-3xl transform translate-x-8 translate-y-8 rotate-12 group-hover:rotate-6 transition-transform duration-500 hidden md:block"></div>
                                
                                {/* Floating Particles */}
                                <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-2xl opacity-50 animate-pulse animation-delay-2000"></div>
                            </div>
                        </motion.div>
                    </motion.div>
                </AnimatePresence>

                {/* Enhanced Pagination Dots */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-gray-200">
                    {slides.map((_, index) => (
                        <motion.button
                            key={index}
                            className={`transition-all duration-300 rounded-full ${
                                currentSlideIndex === index
                                    ? "w-8 h-3 bg-gradient-to-r from-indigo-600 to-purple-600"
                                    : "w-3 h-3 bg-gray-300 hover:bg-gray-400"
                            }`}
                            onClick={() => setCurrentSlideIndex(index)}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                        />
                    ))}
                </div>
            </div>

            {/* Enhanced Contact Side Tab */}
            <motion.div
                className="fixed right-0 top-1/2 transform -translate-y-1/2 z-50 cursor-pointer"
                initial={{ x: 100 }}
                animate={{ x: 0 }}
                transition={{ delay: 1.5, type: "spring", stiffness: 100 }}
                whileHover={{ x: -10, scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsContactOpen(true)}
            >
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white font-heading font-bold py-4 px-6 rounded-l-2xl shadow-2xl flex items-center gap-3 transform -rotate-90 origin-bottom-right hover:shadow-purple-500/50 transition-shadow duration-300">
                    <span className="text-sm uppercase tracking-wider">Contact Us</span>
                    <svg
                        className="w-5 h-5 animate-pulse"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                    </svg>
                </div>
            </motion.div>

            {/* Enhanced WhatsApp Floating Button */}
            <motion.a
                href="https://wa.me/918210345655"
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-4 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 group"
                aria-label="Chat on WhatsApp"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 2, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
            >
                <svg
                    className="w-7 h-7 group-hover:scale-110 transition-transform"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path d="M20.52 3.48A11.77 11.77 0 0012 0C5.39 0 0 5.37 0 12a11.85 11.85 0 001.71 6.13L0 24l6.15-1.62A11.85 11.85 0 0012 24c6.61 0 12-5.37 12-12a11.77 11.77 0 00-3.48-8.52zM12 21.52a9.43 9.43 0 01-4.85-1.31l-.35-.21-3.65.96.98-3.55-.23-.37a9.49 9.49 0 01-1.48-5.13c0-5.26 4.3-9.52 9.6-9.52S21.6 6.74 21.6 12 17.3 21.52 12 21.52zm5.35-7.48c-.29-.14-1.71-.84-1.97-.94s-.46-.14-.65.15-.75.94-.92 1.13-.34.21-.63.07a7.68 7.68 0 01-2.26-1.57 8.58 8.58 0 01-1.59-2.23c-.17-.3 0-.46.13-.61s.3-.34.45-.52a2 2 0 00.3-.5.52.52 0 00-.02-.52c-.07-.15-.64-1.56-.88-2.14-.23-.54-.46-.46-.65-.47h-.56a1.07 1.07 0 00-.77.36A3.2 3.2 0 006.43 9.7c0 1.64 1.17 3.21 1.33 3.44s2.3 3.5 5.58 4.75c.78.3 1.39.47 1.87.6a4.7 4.7 0 002.15.14c.66-.1 2-.81 2.29-1.6s.29-1.46.2-1.6-.26-.21-.54-.35z" />
                </svg>
                
                {/* Pulse Ring Effect */}
                <span className="absolute inset-0 rounded-full bg-green-400 opacity-75 animate-ping"></span>
            </motion.a>

            {/* Contact Form Slider */}
            <AnimatePresence>
                {isContactOpen && (
                    <>
                        {/* Backdrop Overlay */}
                        <motion.div
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90]"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsContactOpen(false)}
                        />
                        
                        {/* Contact Form Panel */}
                        <motion.div
                            className="fixed inset-y-0 right-0 z-[100] w-full max-w-lg bg-white shadow-2xl overflow-y-auto"
                            variants={contactSliderVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 p-6 shadow-lg">
                                <div className="flex justify-between items-center">
                                    <h2 className="font-display text-3xl font-bold text-white">Let's Connect</h2>
                                    <button
                                        onClick={() => setIsContactOpen(false)}
                                        className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                                    >
                                        <svg
                                            className="w-6 h-6"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="p-8">
                                <SliderContactUs onClose={() => setIsContactOpen(false)} />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Custom Styles */}
            <style jsx>{`
                .font-display {
                    font-weight: 900;
                    letter-spacing: -0.02em;
                }

                .font-mono-body {
                    font-family: 'JetBrains Mono', monospace;
                    font-weight: 400;
                    line-height: 1.7;
                }

                .font-heading {
                    font-weight: 700;
                    letter-spacing: 0.02em;
                }

                @keyframes blob {
                    0%, 100% {
                        transform: translate(0, 0) scale(1);
                    }
                    25% {
                        transform: translate(20px, -20px) scale(1.1);
                    }
                    50% {
                        transform: translate(-20px, 20px) scale(0.9);
                    }
                    75% {
                        transform: translate(20px, 20px) scale(1.05);
                    }
                }

                .animate-blob {
                    animation: blob 7s infinite;
                }

                .animation-delay-2000 {
                    animation-delay: 2s;
                }

                .animation-delay-4000 {
                    animation-delay: 4s;
                }

                .bg-size-200 {
                    background-size: 200%;
                }

                .bg-pos-0 {
                    background-position: 0%;
                }

                .bg-pos-100:hover {
                    background-position: 100%;
                }

                @keyframes gradient {
                    0%, 100% {
                        background-position: 0% 50%;
                    }
                    50% {
                        background-position: 100% 50%;
                    }
                }

                .animate-gradient {
                    background-size: 200% auto;
                    animation: gradient 3s ease infinite;
                }
            `}</style>
        </div>
    );
};

export default HeroSection;